import api from './api';

// ============================================
// Types
// ============================================

export interface VoucherStats {
  total_vouchers: number;
  vouchers_ativos: number;
  total_empresas: number;
  total_compras: number;
  receita_total: number;
  total_resgates: number;
}

export interface AdminVoucher {
  id: number;
  codigo: string;
  tipo_id: number;
  tipo_nome: string;
  tipo_categoria: 'valor' | 'desconto';
  empresa_id: number;
  empresa_nome: string | null;
  titulo: string;
  descricao: string | null;
  valor: number | null;
  percentagem_desconto: number | null;
  preco_venda: number;
  quantidade_total: number;
  quantidade_vendida: number;
  data_validade: string | null;
  status: 'rascunho' | 'ativo' | 'esgotado' | 'expirado' | 'cancelado';
  total_vendas: number;
  receita_total: number;
  created_at: string;
}

export interface AdminVoucherTipo {
  id: number;
  nome: string;
  tipo: 'valor' | 'desconto';
  icone: string;
  cor_fundo: string;
  cor_texto: string;
  status: string;
}

export interface AdminVoucherCompra {
  id: number;
  codigo_unico: string;
  status: string;
  preco_pago: number;
  valor_restante: number | null;
  data_compra: string;
  data_uso: string | null;
  beneficiario_nome: string | null;
  voucher_titulo: string;
  voucher_codigo: string;
  empresa_nome: string | null;
  comprador_nome: string | null;
  comprador_email: string | null;
}

export interface AdminVoucherEmpresa {
  ID: string;
  CODIGO: string;
  DESIG: string;
  EMAIL: string | null;
  VOUCHER_ENABLED: number;
  VOUCHER_ENABLED_AT: string | null;
  VOUCHER_ENABLED_BY: string | null;
  TIPO_ENTIDADE: string | null;
  total_vouchers: number;
  total_vendas: number;
}

export interface AdminVoucherResgate {
  id: number;
  valor_resgatado: number | null;
  desconto_aplicado: number | null;
  metodo_validacao: 'qr_code' | 'codigo_manual';
  localizacao: string | null;
  notas: string | null;
  created_at: string;
  codigo_unico: string;
  beneficiario_nome: string | null;
  voucher_titulo: string;
  empresa_nome: string | null;
  operador_nome: string | null;
}

// ============================================
// Service
// ============================================

class AdminVouchersService {
  async getStats(): Promise<VoucherStats> {
    const res = await api.get<{ success: boolean; data?: VoucherStats }>('/admin/vouchers/stats');
    return res.data.data || {
      total_vouchers: 0,
      vouchers_ativos: 0,
      total_empresas: 0,
      total_compras: 0,
      receita_total: 0,
      total_resgates: 0,
    };
  }

  async listVouchers(params?: {
    status?: string;
    tipo_id?: number;
    empresa_id?: number;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<AdminVoucher[]> {
    const qs = new URLSearchParams();
    if (params?.status) qs.append('status', params.status);
    if (params?.tipo_id) qs.append('tipo_id', String(params.tipo_id));
    if (params?.empresa_id) qs.append('empresa_id', String(params.empresa_id));
    if (params?.search) qs.append('search', params.search);
    if (params?.limit) qs.append('limit', String(params.limit));
    if (params?.offset) qs.append('offset', String(params.offset));
    const url = `/admin/vouchers${qs.toString() ? `?${qs.toString()}` : ''}`;
    const res = await api.get<{ success: boolean; data?: AdminVoucher[] }>(url);
    return res.data.data || [];
  }

  async listTipos(): Promise<AdminVoucherTipo[]> {
    const res = await api.get<{ success: boolean; data?: AdminVoucherTipo[] }>('/admin/vouchers/tipos');
    return res.data.data || [];
  }

  async listCompras(params?: { limit?: number; offset?: number }): Promise<AdminVoucherCompra[]> {
    const qs = new URLSearchParams();
    if (params?.limit) qs.append('limit', String(params.limit));
    if (params?.offset) qs.append('offset', String(params.offset));
    const url = `/admin/vouchers/compras${qs.toString() ? `?${qs.toString()}` : ''}`;
    const res = await api.get<{ success: boolean; data?: AdminVoucherCompra[] }>(url);
    return res.data.data || [];
  }

  async listEmpresas(params?: { enabled?: 0 | 1; search?: string }): Promise<AdminVoucherEmpresa[]> {
    const qs = new URLSearchParams();
    if (params?.enabled !== undefined) qs.append('enabled', String(params.enabled));
    if (params?.search) qs.append('search', params.search);
    const url = `/admin/vouchers/empresas${qs.toString() ? `?${qs.toString()}` : ''}`;
    const res = await api.get<{ success: boolean; data?: AdminVoucherEmpresa[]; needsMigration?: boolean }>(url);
    return res.data.data || [];
  }

  async toggleEmpresa(id: string, enabled: boolean): Promise<void> {
    const res = await api.post<{ success: boolean; error?: string }>(
      `/admin/vouchers/empresas/${id}/toggle`,
      { enabled }
    );
    if (!res.data.success) {
      throw new Error(res.data.error || 'Falha ao alterar estado do serviço');
    }
  }

  async listResgates(params?: { limit?: number; offset?: number }): Promise<AdminVoucherResgate[]> {
    const qs = new URLSearchParams();
    if (params?.limit) qs.append('limit', String(params.limit));
    if (params?.offset) qs.append('offset', String(params.offset));
    const url = `/admin/vouchers/resgates${qs.toString() ? `?${qs.toString()}` : ''}`;
    const res = await api.get<{ success: boolean; data?: AdminVoucherResgate[] }>(url);
    return res.data.data || [];
  }
}

const adminVouchersService = new AdminVouchersService();
export default adminVouchersService;
